import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, AlertDescription, Badge, Button, Drawer, DrawerBody, DrawerContent,
  DrawerHeader, DrawerTitle, Spinner,
} from '@/components/ui'
import { useFeature } from '@/hooks'
import { useCredentialSchemas, useTestCredential } from '@/hooks/useCredentialSchemas'
import { useIntegrationsOverview } from '@/hooks/useIntegrationsOverview'
import { apiClient } from '@/services/apiClient'
import { formatRelativeTime } from '@/utils'
import type { CredentialFieldSchema } from '@/types/credentialSchemas'
import { entryHealthChip } from './entryHealth'
import { EditServicePanel } from './EditServicePanel'

export interface SetEntryDetail {
  id: string
  service: string
  name: string
  config: Record<string, string> | null
  has_key: boolean
  created_at: string | null
  updated_at: string | null
  secrets_set: Record<string, boolean>
}

interface CredentialSetSummary {
  id: string
  name: string
  services: string[]
  assigned_user_count: number
}

function provenance(field: CredentialFieldSchema, config: Record<string, string> | null) {
  const stored = config?.[field.key]
  if (stored !== undefined) {
    // Explicit "" = "send none" — it overrides the adapter default (the
    // merge-on-save contract), so it is stored, not default.
    return {
      value: stored === '' ? 'None (explicitly cleared)' : stored,
      chip: <Badge variant="success">Stored</Badge>,
      muted: stored === '',
    }
  }
  if (field.default_value) {
    return {
      value: field.default_value,
      chip: <Badge variant="secondary">{field.default_label || 'Default'}</Badge>,
      muted: true,
    }
  }
  return { value: '—', chip: <Badge variant="outline">Unset</Badge>, muted: true }
}

export function IntegrationDrawer({
  serviceKey,
  source,
  onClose,
}: {
  serviceKey: string
  source: 'registry' | 'oauth'
  onClose: () => void
}) {
  const overview = useIntegrationsOverview()
  const row = overview.data?.find((s) => s.service === serviceKey && s.source === source) ?? null
  const [setId, setSetId] = useState<string | null>(null)
  // First load (or row change) picks the first set once; user picks after.
  useEffect(() => {
    if (row && setId === null && row.entries.length > 0) setSetId(row.entries[0].set_id)
  }, [row, setId])
  // Row vanished after a refetch (e.g. oauth set deleted elsewhere) — close.
  useEffect(() => {
    if (overview.isSuccess && !row) onClose()
  }, [overview.isSuccess, row, onClose])

  const toolFramework = useFeature('tool_framework')
  const schemas = useCredentialSchemas()
  const schema = schemas.data?.find((s) => s.service === row?.service)
  const queryClient = useQueryClient()

  const [mode, setMode] = useState<'read' | 'edit' | 'create'>('read')
  const [createTargetId, setCreateTargetId] = useState<string | null>(null)
  // Carries the set it belongs to, so it only renders while that set is the
  // one being viewed — immune to setId-transition ordering (a create-save
  // changes setId in the same tick it sets the notice; keying on setId means
  // there's no dependence on effect-vs-state-update scheduling to show it).
  const [notice, setNotice] = useState<{ setId: string | null; text: string } | null>(null)
  const [testState, setTestState] = useState<{ running: boolean; detail: string | null; ok: boolean | null }>(
    { running: false, detail: null, ok: null },
  )
  const { mutateAsync: testCredential } = useTestCredential()
  // Switching sets exits any in-progress edit/test state from the previous
  // set — none of it applies to the newly picked one. (The notice is NOT
  // reset here: it's keyed by setId above, so it naturally hides when setId
  // no longer matches it — no dependency on this effect's timing.)
  useEffect(() => {
    setMode('read')
    setTestState({ running: false, detail: null, ok: null })
  }, [setId])

  const setsList = useQuery({
    queryKey: ['credential-sets-summary'],
    enabled: mode !== 'read',
    queryFn: async () =>
      apiClient.get<CredentialSetSummary[]>('/api/admin/credential-sets'),
  })
  const setsWithoutService =
    setsList.data?.filter((s) => !s.services.includes(row?.service ?? '')) ?? []
  const assignedUserCount = (targetId: string | null) =>
    setsList.data?.find((s) => s.id === targetId)?.assigned_user_count ?? null

  const detail = useQuery({
    queryKey: ['credential-set-detail', setId],
    enabled: !!setId && row !== null && row.source === 'registry',
    queryFn: async () =>
      apiClient.get<{ entries: SetEntryDetail[] }>(`/api/admin/credential-sets/${setId}`),
  })
  const entry = detail.data?.entries.find((e) => e.service === row?.service) ?? null
  const pickedSet = row?.entries.find((e) => e.set_id === setId) ?? null
  const configFields = schema?.fields.filter((f) => f.storage === 'config') ?? []
  const secretField = schema?.fields.find((f) => f.storage === 'secret')

  async function runTest() {
    if (!setId || !row) return
    setTestState({ running: true, detail: null, ok: null })
    try {
      const r = await testCredential({ setId, service: row.service })
      setTestState({ running: false, ok: r.ok, detail: r.detail || (r.ok ? 'Connected' : 'Connection failed') })
    } catch (err) {
      setTestState({ running: false, ok: false, detail: err instanceof Error ? err.message : 'Test failed' })
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['integrations-overview'] })
      await queryClient.invalidateQueries({ queryKey: ['credential-set-detail', setId] })
    }
  }

  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="max-w-xl">
        <DrawerHeader>
          <DrawerTitle>{row?.label ?? ''}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="space-y-6">
          {overview.isLoading ? (
            <div className="flex justify-center py-8"><Spinner className="w-6 h-6" /></div>
          ) : overview.isError && !row ? (
            <Alert variant="warning">
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>
                  Couldn't load integrations
                  {overview.error instanceof Error ? ` — ${overview.error.message}` : ''}
                </span>
                <button
                  type="button"
                  className="text-sm font-medium underline"
                  onClick={() => overview.refetch()}
                >
                  Retry
                </button>
              </AlertDescription>
            </Alert>
          ) : !row ? null : row.source === 'oauth' ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground">OAuth tokens — managed by Connect</p>
              {row.entries.map((e) => (
                <p key={e.set_id} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span>
                    {e.set_name}
                    {e.updated_at &&
                      ` · updated ${formatRelativeTime(new Date(e.updated_at).getTime())}`}
                  </span>
                  {e.live === true && <Badge variant="success">Active</Badge>}
                  {e.live === false && <Badge variant="outline">Superseded</Badge>}
                </p>
              ))}
              {toolFramework && (
                <Link to="/admin/tools" className="text-sm text-primary hover:underline">
                  Open Tool Library →
                </Link>
              )}
            </div>
          ) : schemas.isLoading ? (
            <div className="flex justify-center py-8"><Spinner className="w-6 h-6" /></div>
          ) : schemas.isError ? (
            <Alert variant="warning">
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>
                  Couldn't load schemas
                  {schemas.error instanceof Error ? ` — ${schemas.error.message}` : ''}
                </span>
                <button
                  type="button"
                  className="text-sm font-medium underline"
                  onClick={() => schemas.refetch()}
                >
                  Retry
                </button>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {notice && notice.setId === setId && (
                <Alert variant="info" data-testid="saved-notice">
                  <AlertDescription>{notice.text}</AlertDescription>
                </Alert>
              )}

              {mode === 'create' ? (
                <>
                  {setsList.isLoading ? (
                    <div className="flex justify-center py-8"><Spinner className="w-6 h-6" /></div>
                  ) : setsList.isError ? (
                    <Alert variant="warning">
                      <AlertDescription className="flex items-center justify-between gap-4">
                        <span>
                          Couldn't load credential sets
                          {setsList.error instanceof Error ? ` — ${setsList.error.message}` : ''}
                        </span>
                        <button
                          type="button"
                          className="text-sm font-medium underline"
                          onClick={() => setsList.refetch()}
                        >
                          Retry
                        </button>
                      </AlertDescription>
                    </Alert>
                  ) : setsWithoutService.length === 0 ? (
                    <Alert variant="info">
                      <AlertDescription>
                        No credential sets yet that this service can be added to.{' '}
                        Create one on the{' '}
                        <Link to="/admin/credential-sets" className="underline">
                          Advanced: credential sets
                        </Link>{' '}
                        page first.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <label className="block text-sm">
                        <span className="text-muted-foreground">Add to credential set</span>
                        <select
                          aria-label="Add to credential set"
                          className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={
                            setsWithoutService.find((s) => s.id === createTargetId)?.id
                              ?? setsWithoutService[0].id
                          }
                          onChange={(e) => setCreateTargetId(e.target.value)}
                        >
                          {setsWithoutService.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </label>
                      {schema && (() => {
                        const targetId = createTargetId ?? setsWithoutService[0].id
                        const target = setsWithoutService.find((s) => s.id === targetId)
                          ?? setsWithoutService[0]
                        return (
                          <EditServicePanel
                            row={row}
                            schema={schema}
                            mode="create"
                            setId={target.id}
                            setName={target.name}
                            assignedUserCount={assignedUserCount(target.id)}
                            entry={null}
                            onSaved={() => {
                              // EditServicePanel's own invalidate() only covers
                              // integrations-overview + credential-set-detail —
                              // the sets-list query (which drives "sets without
                              // this service" for both the create picker and
                              // the "+ Add to another set" affordance) has its
                              // own 5-minute app-wide staleTime and must be
                              // invalidated here or it'd keep offering the set
                              // just added as a target (upsert would silently
                              // rotate the key just stored).
                              void queryClient.invalidateQueries({ queryKey: ['credential-sets-summary'] })
                              setNotice({
                                setId: target.id,
                                text: `Added to "${target.name}". Connection verification has not run yet.`,
                              })
                              setMode('read')
                              setCreateTargetId(null)
                              setTestState({ running: false, detail: null, ok: null })
                              setSetId(target.id)
                            }}
                            onCancel={() => { setMode('read'); setCreateTargetId(null) }}
                            onRemoved={() => setMode('read')}
                          />
                        )
                      })()}
                    </>
                  )}
                </>
              ) : row.entries.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Not configured in any credential set.
                  </p>
                  <Button size="sm" onClick={() => { setNotice(null); setMode('create') }}>
                    Configure…
                  </Button>
                  {schema?.acquisition && (
                    <AcquisitionLink
                      consoleUrl={schema.acquisition.console_url}
                      instructions={schema.acquisition.instructions}
                    />
                  )}
                </div>
              ) : (
                <>
                  {row.entries.length > 1 && (
                    <label className="block text-sm">
                      <span className="text-muted-foreground">Credential set</span>
                      <select
                        aria-label="Credential set"
                        className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={setId ?? ''}
                        onChange={(e) => setSetId(e.target.value)}
                        disabled={mode === 'edit'}
                      >
                        {row.entries.map((e) => (
                          <option key={e.set_id} value={e.set_id}>{e.set_name}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  {detail.isLoading && (
                    <div className="flex justify-center py-8"><Spinner className="w-6 h-6" /></div>
                  )}
                  {detail.isError && (
                    <Alert variant="warning">
                      <AlertDescription className="flex items-center justify-between gap-4">
                        <span>
                          Couldn't load set details
                          {detail.error instanceof Error ? ` — ${detail.error.message}` : ''}
                        </span>
                        <button
                          type="button"
                          className="text-sm font-medium underline"
                          onClick={() => detail.refetch()}
                        >
                          Retry
                        </button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {entry && pickedSet && (
                    mode === 'edit' && schema && setId ? (
                      <>
                        <EditServicePanel
                          row={row}
                          schema={schema}
                          mode="edit"
                          setId={setId}
                          setName={pickedSet.set_name}
                          assignedUserCount={assignedUserCount(setId)}
                          entry={entry}
                          onSaved={(n) => {
                            setMode('read')
                            // The saved config may have changed what a prior probe verified —
                            // don't leave a now-stale success/failure detail on screen.
                            setTestState({ running: false, detail: null, ok: null })
                            setNotice({
                              setId,
                              text: `Saved ${n} change${n === 1 ? '' : 's'} to "${pickedSet.set_name}". ` +
                                'Connection verification was reset — the key has not been tested since this change.',
                            })
                          }}
                          onCancel={() => setMode('read')}
                          onRemoved={() => {
                            setMode('read')
                            // The removed entry's set may no longer exist in row.entries —
                            // null it out so the auto-select effect (guarded on setId === null)
                            // can land on a surviving set instead of dead-ending on the gone one.
                            setSetId(null)
                            setTestState({ running: false, detail: null, ok: null })
                          }}
                        />
                        {setsWithoutService.length > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => setMode('create')}>
                            + Add to another set…
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <div data-testid="secret-row" className="rounded-md border border-border p-3 space-y-1">
                          <p className="text-sm font-medium text-foreground">
                            {secretField?.label ?? 'API Key'}: {entry.has_key ? 'Key set' : '—'}
                          </p>
                          {entry.updated_at && (
                            <p className="text-sm text-muted-foreground">
                              Last rotated {formatRelativeTime(new Date(entry.updated_at).getTime())}
                            </p>
                          )}
                          <div>{entryHealthChip(pickedSet, row.probe_available)}</div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setMode('edit') }}>
                              Edit
                            </Button>
                            {row.probe_available && (
                              <Button size="sm" variant="secondary" disabled={testState.running}
                                      isLoading={testState.running} onClick={() => void runTest()}>
                                Test connection
                              </Button>
                            )}
                          </div>
                          {testState.detail !== null && !testState.running && (
                            <p className={testState.ok ? 'text-sm text-success' : 'text-sm text-destructive'}>
                              {testState.detail}
                            </p>
                          )}
                        </div>

                        {configFields.length > 0 && (
                          <ul className="divide-y divide-border rounded-md border border-border">
                            {configFields.map((f) => {
                              const p = provenance(f, entry.config)
                              return (
                                <li
                                  key={f.key}
                                  data-testid={`field-${f.key}`}
                                  className="flex items-center gap-3 px-3 py-2"
                                >
                                  <span className="text-sm text-foreground flex-1 min-w-0 truncate">
                                    {f.label}
                                  </span>
                                  <span
                                    className={
                                      p.muted
                                        ? 'text-sm text-muted-foreground italic'
                                        : 'text-sm text-foreground'
                                    }
                                  >
                                    {p.value}
                                  </span>
                                  {p.chip}
                                </li>
                              )
                            })}
                          </ul>
                        )}

                        {schema?.acquisition && (
                          <AcquisitionLink
                            consoleUrl={schema.acquisition.console_url}
                            instructions={schema.acquisition.instructions}
                          />
                        )}
                      </>
                    )
                  )}
                </>
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

function AcquisitionLink({ consoleUrl, instructions }: { consoleUrl: string; instructions: string }) {
  if (!consoleUrl) return null
  return (
    <div className="space-y-1">
      <a
        href={consoleUrl}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-primary hover:underline"
      >
        Get your key →
      </a>
      <p className="text-xs text-muted-foreground">{instructions}</p>
    </div>
  )
}
